// src/hooks/useFirebase.js
import { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

// Error boundary for Firebase operations
class FirebaseError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
    this.name = "FirebaseError";
  }
}

const useFirebase = (gameType) => {
  const [userId, setUserId] = useState(null);
  const [level, setLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(1);
  const [levelsData, setLevelsData] = useState({});
  const [bestScores, setBestScores] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoized collection names based on game type
  const getCollectionName = useCallback(() => {
    const collections = {
      arabicFlagGuesser: 'countries',
      ArabicHangman: 'levels',
      WordMatchGameV2: 'levels',
      wordMatchGame: 'levels', // Legacy support
      AudioQuizGame: 'levels',
      arabicNumberSpeed: 'numbers' // Specific collection for number game
    };
    return collections[gameType] || 'levels';
  }, [gameType]);

  const loadUserData = useCallback(async (uid) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        // User doesn't exist yet, create with default data
        const defaultData = {
          progress: {
            [gameType]: {
              currentLevel: 1,
              maxLevel: 1,
              levels: {}
            }
          }
        };
        await setDoc(doc(db, 'users', uid), defaultData);
        setLevel(1);
        setMaxLevel(1);
        setBestScores({});
        return;
      }

      const data = userDoc.data();
      const gameData = data.progress?.[gameType] || {};
      
      setLevel(gameData.currentLevel || 1);
      // FIXED: Use gameData.maxLevel instead of currentLevel
      setMaxLevel(gameData.maxLevel || gameData.currentLevel || 1);
      setBestScores(gameData.levels || {});

    } catch (error) {
      console.error('Error loading user data:', error);
      setError(error);
      // Fallback to local storage if available
      const localData = localStorage.getItem(`gameProgress_${gameType}`);
      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          setLevel(parsed.level || 1);
          setMaxLevel(parsed.maxLevel || 1);
          setBestScores(parsed.bestScores || {});
        } catch (e) {
          console.error('Error parsing local storage data:', e);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [gameType]);

  const loadLevelsData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const collectionName = getCollectionName();
      const snapshot = await getDocs(collection(db, collectionName));
      
      const data = {};
      snapshot.forEach(doc => {
        data[doc.id] = doc.data();
      });
      
      setLevelsData(data);
      
      // Cache data in local storage
      localStorage.setItem(`gameLevels_${gameType}`, JSON.stringify(data));
      
      return data;
    } catch (error) {
      console.error('Error loading levels data:', error);
      setError(error);
      
      // Fallback to local storage
      const localData = localStorage.getItem(`gameLevels_${gameType}`);
      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          setLevelsData(parsed);
          return parsed;
        } catch (e) {
          console.error('Error parsing local levels data:', e);
        }
      }
      
      return {};
    } finally {
      setIsLoading(false);
    }
  }, [gameType, getCollectionName]);

  const saveLevelData = useCallback(async (levelNum, scoreData) => {
    if (!userId) {
      // Save to local storage if not authenticated
      const localData = {
        level: levelNum,
        maxLevel: Math.max(levelNum, maxLevel),
        bestScores: {
          ...bestScores,
          [`level${levelNum}`]: scoreData
        }
      };
      localStorage.setItem(`gameProgress_${gameType}`, JSON.stringify(localData));
      return true;
    }
    
    const userRef = doc(db, 'users', userId);
    const levelKey = `level${levelNum}`;
    
    try {
      const currentBest = bestScores[levelKey] || {};
      const currentBestScore = currentBest.bestScore || currentBest.totalScore || 0;
      const isNewBest = !currentBest || scoreData.totalScore > currentBestScore;
      
      const dataToSave = {
        ...scoreData,
        bestScore: isNewBest ? scoreData.totalScore : currentBestScore,
        bestStars: isNewBest ? scoreData.stars : Math.max(scoreData.stars, currentBest.stars || 0),
        bestAccuracy: isNewBest ? scoreData.accuracy : Math.max(scoreData.accuracy, currentBest.accuracy || 0),
        completedAt: new Date().toISOString(),
        latestScore: scoreData.totalScore,
        latestStars: scoreData.stars,
        latestAccuracy: scoreData.accuracy
      };
      
      // FIXED: Properly save both currentLevel and maxLevel
      await setDoc(userRef, {
        progress: {
          [gameType]: {
            currentLevel: Math.max(levelNum, level),
            maxLevel: Math.max(levelNum, maxLevel), // FIXED: Save maxLevel properly
            levels: {
              ...bestScores, // FIXED: Keep existing scores
              [levelKey]: dataToSave
            }
          }
        }
      }, { merge: true });
      
      // Update local state
      setBestScores(prev => ({
        ...prev,
        [levelKey]: dataToSave
      }));
      
      setMaxLevel(prev => Math.max(prev, levelNum));
      
      return true;
    } catch (error) {
      console.error('Error saving level data:', error);
      return false;
    }
  }, [userId, gameType, level, maxLevel, bestScores]);

  const updateLevel = useCallback((newLevel) => {
    if (newLevel <= maxLevel && levelsData[newLevel.toString()]) {
      setLevel(newLevel);
      return true;
    }
    return false;
  }, [maxLevel, levelsData]);

  // FIXED: unlockNextLevel now saves to database
  const unlockNextLevel = useCallback(async () => {
    const nextLevel = level + 1;
    
    console.log('unlockNextLevel called:', { 
      currentLevel: level, 
      nextLevel, 
      maxLevel, 
      levelExists: !!levelsData[nextLevel.toString()] 
    });
    
    if (!levelsData[nextLevel.toString()]) {
      console.log('Next level does not exist in levelsData');
      return false;
    }

    const newMaxLevel = Math.max(maxLevel, nextLevel);
    
    // Update local state immediately
    setMaxLevel(newMaxLevel);
    
    // Save to database if user is authenticated
    if (userId) {
      try {
        const userRef = doc(db, 'users', userId);
        await setDoc(userRef, {
          progress: {
            [gameType]: {
              currentLevel: level, // Keep current level
              maxLevel: newMaxLevel, // Update maxLevel
              levels: bestScores // Keep existing scores
            }
          }
        }, { merge: true });
        
        console.log('Successfully saved maxLevel to database:', newMaxLevel);
      } catch (error) {
        console.error('Error saving maxLevel to database:', error);
        return false;
      }
    } else {
      // Save to local storage if not authenticated
      const localData = {
        level: level,
        maxLevel: newMaxLevel,
        bestScores: bestScores
      };
      localStorage.setItem(`gameProgress_${gameType}`, JSON.stringify(localData));
    }
    
    return true;
  }, [level, levelsData, maxLevel, userId, gameType, bestScores]);

  const getBestScore = useCallback((levelNum) => {
    return bestScores[`level${levelNum}`] || null;
  }, [bestScores]);

  const isLevelUnlocked = useCallback((levelNum) => {
    return levelNum <= maxLevel;
  }, [maxLevel]);

  const resetAllProgress = useCallback(async () => {
    try {
      if (userId) {
        await setDoc(doc(db, 'users', userId), {
          progress: {
            [gameType]: {
              currentLevel: 1,
              maxLevel: 1,
              levels: {}
            }
          }
        }, { merge: true });
      }
      
      // Clear local storage
      localStorage.removeItem(`gameProgress_${gameType}`);
      
      // Reset state
      setLevel(1);
      setMaxLevel(1);
      setBestScores({});
      
      return true;
    } catch (error) {
      console.error('Error resetting progress:', error);
      return false;
    }
  }, [userId, gameType]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserId(user.uid);
        loadUserData(user.uid);
      } else {
        setUserId(null);
        setLevel(1);
        setMaxLevel(1);
        setBestScores({});
        setIsLoading(false);
      }
    });
    return () => unsubscribe();
  }, [gameType, loadUserData]);

  // Load levels data on mount
  useEffect(() => {
    loadLevelsData();
  }, [loadLevelsData]);

  return {
    // State
    userId,
    level,
    setLevel,
    maxLevel,
    levelsData,
    bestScores,
    isLoading,
    error,
    
    // Actions
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    getBestScore,
    isLevelUnlocked,
    resetAllProgress,
    
    // Utilities
    reloadUserData: () => userId && loadUserData(userId),
    reloadLevelsData: loadLevelsData
  };
};

export default useFirebase;